import React, { useState } from "react";
import { Clock, Camera, Video } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Stars } from "@/components/Stars";
import MediaPicker from "@/components/MediaPicker";
import { uploadPhoto, uploadVideo } from "@/lib/media";
import { Link } from "react-router-dom";
import { storage } from "@/lib/storage";

const initialMedia = (mine) => {
  if (mine?.video_url) return { kind: "video", url: mine.video_url, poster: mine.video_poster_url };
  if (mine?.photo_url) return { kind: "photo", url: mine.photo_url };
  return null;
};

/** Rating + text + one photo/video. `onSubmit({rating, text, photo_id, video_id})` must resolve to the saved review or throw. */
export default function ReviewForm({ mine, onSubmit, defaults = { name: "", email: "" } }) {
  const { t } = useLang();
  const [rating, setRating] = useState(mine?.rating || 0);
  const [name, setName] = useState(defaults.name);
  const [email, setEmail] = useState(defaults.email);
  const [text, setText] = useState(mine?.text || "");
  const [media, setMedia] = useState(() => initialMedia(mine));
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rules, setRules] = useState(() => storage.get("atlas.rulesAccepted") === "1");

  const uploadMedia = async () => {
    if (!media?.file) return {};
    if (media.kind === "video") return { video_id: await uploadVideo(media.file, setProgress) };
    setProgress(50);
    return { photo_id: await uploadPhoto(media.file) };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { setError(t("review_pick_rating")); return; }
    if (name.trim().length < 2) { setError(t("review_name_required")); return; }
    if (!rules) { setError(t("rules_required")); return; }
    storage.set("atlas.rulesAccepted", "1");
    setBusy(true); setError(""); setProgress(0);
    try {
      const saved = await onSubmit({ rating, text, name: name.trim(), email: email.trim(), ...(await uploadMedia()) });
      setMedia(initialMedia(saved));
    } catch {
      setError(t("review_error"));
    }
    setProgress(0);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="rounded-xl border-2 border-dashed border-ochre/40 bg-ochre/5 p-3 space-y-2" data-testid="review-form">
      <div className="flex items-start gap-3">
        <span className="flex items-center gap-1 text-ochre mt-0.5"><Camera className="w-5 h-5" /><Video className="w-5 h-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-ink">{mine ? t("review_edit") : t("media_cta")}</span>
          <span className="block text-xs text-muted-foreground leading-snug">{t("media_cta_sub")}</span>
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{t("review_write")}</span>
        <Stars value={rating} onChange={setRating} size="w-5 h-5" testid="review-rating" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} required placeholder={t("review_name_placeholder")}
          className="w-full rounded-lg border border-stoneLine bg-card px-3 py-2 text-sm focus:outline-none focus:border-ochre" data-testid="review-name" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("review_email_placeholder")}
          className="w-full rounded-lg border border-stoneLine bg-card px-3 py-2 text-sm focus:outline-none focus:border-ochre" data-testid="review-email" />
      </div>
      <p className="text-[10px] text-muted-foreground -mt-1">{t("review_email_hint")}</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={600} rows={3} placeholder={t("review_placeholder")}
        className="w-full rounded-lg border border-stoneLine bg-card px-3 py-2 text-sm focus:outline-none focus:border-ochre" data-testid="review-text" />
      <MediaPicker media={media} setMedia={setMedia} progress={progress} />
      <label className="flex items-start gap-2 text-xs text-ink/80 cursor-pointer" data-testid="rules-accept-label">
        <input type="checkbox" checked={rules} onChange={(e) => setRules(e.target.checked)} className="mt-0.5 accent-[#C2552B]" data-testid="rules-accept" />
        <span>{t("rules_accept").split("{rules}")[0]}<Link to="/regole" target="_blank" className="text-ochre underline" data-testid="rules-link">{t("rules_title")}</Link>{t("rules_accept").split("{rules}")[1]}</span>
      </label>
      <div className="flex items-center justify-between gap-2">
        {mine?.status === "pending" ? (
          <span className="inline-flex items-center gap-1 text-xs text-ochre" data-testid="review-pending"><Clock className="w-3 h-3" /> {t("review_pending")}</span>
        ) : <span className="text-xs text-muted-foreground">{text.length}/600</span>}
        <button type="submit" disabled={busy || text.trim().length < 3} className="px-4 py-1.5 rounded-full bg-ink text-sand text-xs font-medium hover:bg-ochre disabled:opacity-50 transition-colors" data-testid="review-submit">
          {busy && media?.kind === "video" && progress > 0 && progress < 100 ? `${t("review_uploading")} ${progress}%` : t("review_send")}
        </button>
      </div>
      {error && <p className="text-xs text-red-600" data-testid="review-error">{error}</p>}
    </form>
  );
}
